import React, { useState, useEffect, ChangeEvent, ClipboardEvent, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/main-logo.svg';
import { deleteFromStorage, getFromStorage, maskEmail, saveToStorage } from '../../utils/Helper';
import { ApiTypes, OtpVerifyTypes, StorageConstants } from '../../constants/Constants';
import Button from '../../components/ui/Button';
import useMakeRequest from '../../utils/ApiHelper';
import toast from 'react-hot-toast';
import { ApiConstants } from '../../constants/ApiConstants';
import { Routes } from '../../constants/RouteConstants';
import authContext from '../../context/authContext';
import CustomInput from '../../components/ui/CustomInput';

const Otp: React.FC = () => {

  const initialTime = 30;
  const { state } = useLocation();
  const profile: any = useContext(authContext);
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [fullOtp, setFullOtp] = useState<string>('');
  const [resendTimer, setResendTimer] = useState<number>(initialTime);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
  const verifyOtp = useMakeRequest();
  const resendOtp = useMakeRequest();


  useEffect(() => {
    const firstInput = document.getElementById('otp-0') as HTMLInputElement | null;
    if (firstInput) {
      firstInput.focus();
    }
    const savedStartTime = getFromStorage({ key: StorageConstants?.OTP_START_TIME, type: 'session' });
    if (savedStartTime) {
      const timeElapsed = Math.floor((Date.now() - Number(savedStartTime)) / 1000);
      const remainingTime = initialTime - timeElapsed;
      if (remainingTime > 0) {
        setResendTimer(remainingTime);
        setIsTimerActive(true);
      } else {
        setResendTimer(0);
        setIsTimerActive(false);
      }
    } else {
      saveToStorage({ key: StorageConstants?.OTP_START_TIME, value: Date.now().toString(), type: 'session' })
    }


  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isTimerActive) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerActive]);

  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    const newOtp = [...otp];

    if (!/^\d*$/.test(value)) {
      return;
    }

    if (value.length === 0) {
      newOtp[index] = '';
      setOtp(newOtp);

      if (index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement | null;
        if (prevInput) {
          prevInput.focus();
        }
      }
    } else if (value.length === 1) {
      newOtp[index] = value;
      setOtp(newOtp);
      if (index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement | null;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    if (!/^\d*$/.test(pasteData)) {
      return;
    }

    const newOtp = [...otp];
    for (let i = 0; i < pasteData.length; i++) {
      if (index + i < otp.length) {
        newOtp[index + i] = pasteData[i];
      }
    }

    setOtp(newOtp);
    const nextInputIndex = Math.min(index + pasteData.length, otp.length - 1);
    const nextInput = document.getElementById(`otp-${nextInputIndex}`) as HTMLInputElement | null;
    if (nextInput) {
      nextInput.focus();
    }
  };

  const handleResendOtp = async () => {
    if (!isTimerActive) {
      toast.promise(
        resendOtp.makeRequest({ payload: { email: state?.email, verify_type: state?.verifyType }, url: ApiConstants?.ResendOtp }),
        {
          loading: 'Processing...',
          success: (data: any) => data.message,
          error: (error: any) => error.message,
        }
      );
    }
  };



  const handleSubmit = () => {
    if (fullOtp?.length < 4) {
      toast.dismiss()
      return toast.error("Please enter 4 digit Verification code.")
    }
    const payload = {
      email: state?.email,
      otp: fullOtp,
      verify_type: state?.verifyType
    }
    let apiUrl = ''
    if (state?.verifyType === OtpVerifyTypes?.MAIL_VERIFY) {
      apiUrl = ApiConstants?.VerifyOtp
    } else if (state?.verifyType === OtpVerifyTypes?.FORGOT_PASSWORD) {
      apiUrl = ApiConstants?.ResetPassword
      payload.verify_type = 'OTP_VERIFY'
    }
    toast.promise(
      verifyOtp.makeRequest({ payload, url: apiUrl }),
      {
        loading: 'Verifying...',
        success: (data: any) => data.message,
        error: (error: any) => error.message,
      }
    );
  };

  useEffect(() => {
    if (!state?.email || !state?.verifyType) {
      navigate(-1)
    }

    window.addEventListener('popstate', () => {
      deleteFromStorage({ key: StorageConstants?.OTP_START_TIME, type: 'session' })
    });
  }, [state])

  useEffect(() => {
    if (otp) {
      const otpCode = otp.join('');
      setFullOtp(otpCode)
    }
  }, [otp])

  useEffect(() => {
    if (verifyOtp?.status) {
      if (state?.verifyType === OtpVerifyTypes?.MAIL_VERIFY) {
        saveToStorage({ key: StorageConstants?.ACCESS_TOKEN, value: verifyOtp?.data?.access_token })
        saveToStorage({ key: StorageConstants?.USER_DETAIL, value: verifyOtp?.data?.data })

        navigate(Routes?.ManageProfile)
        profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET })
      } else if (state?.verifyType === OtpVerifyTypes?.FORGOT_PASSWORD) {
        // navigate(Routes?.ChangePassword)
        navigate(Routes?.ResetPassword, { state: { email: state?.email, otp: fullOtp } })
      }
    }
  }, [verifyOtp?.status])

  useEffect(() => {
    if (resendOtp?.status) {
      saveToStorage({ key: StorageConstants?.OTP_START_TIME, value: Date.now().toString(), type: 'session' })
      setResendTimer(initialTime);
      setIsTimerActive(true);
    }
  }, [resendOtp?.status])

  return (
    <main id="main-content" className="auth-page otp-page">
      <div className="auth-container">
        <h1 className='auth-title'>Verify Your Email</h1>
        <p className='auth-sub-para'>A 4-digit verification code was just sent to {maskEmail(state?.email)}</p>

        <div className='auth-form-wrap'>
          <div className='otp-inputs'>
            {otp.map((value, index) => (
              <input
                key={index}
                type="text"
                className='form-control'
                id={`otp-${index}`}
                aria-label={`Digit ${index + 1} of verification code`}
                value={value}
                onChange={(e) => handleOtpChange(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSubmit()
                  }
                }}
                maxLength={1}
                inputMode="numeric"
              />
            ))}
          </div>

          <Button
            type="button"
            className='resend-otp'
            onClick={handleResendOtp}
            disabled={isTimerActive}
            loading={resendOtp?.loading}
          >
            {isTimerActive ? `Resend in : ${resendTimer} sec` : 'Resend'}
          </Button>

          <Button
            type="button"
            className='com-btn'
            onClick={handleSubmit}
            loading={verifyOtp?.loading}
          >
            Submit
          </Button>
        </div>
      </div>
    </main>
  );
};

export default Otp;